/**
 * هويات بصرية جاهزة لمساحة المنصة فقط.
 *
 * المنصة ليست شركة: ثيماتها مصمّمة لواجهات التحكم والإدارة — أسطح محددة،
 * شرائط جانبية هادئة، ولمسات نيون محسوبة — ومختلفة عن ثيمات الشركات والعلامات
 * والفرق. العدد: 5 نهارية + 5 ليلية + 5 تدريجات نهارية + 5 تدريجات ليلية.
 */

import { DEFAULT_THEME } from '@/domain/settings';
import type { ThemeTokens } from '@/domain/types';
import { FONT_PRESETS, type ThemePreset } from './theme-presets';

function fontFamily(id: string): string {
  return FONT_PRESETS.find((font) => font.id === id)?.family ?? DEFAULT_THEME.fontFamily;
}

const FONT_SYSTEM = fontFamily('system');
const FONT_IBM = fontFamily('ibm');
const FONT_CAIRO = fontFamily('cairo');
const FONT_TAJAWAL = fontFamily('tajawal');
const FONT_ALMARAI = fontFamily('almarai');

export interface PlatformThemeGroup {
  id: 'day' | 'night' | 'day-gradient' | 'night-gradient';
  label: string;
  hint: string;
  icon: 'sun' | 'moon';
  items: ThemePreset[];
}

/** مجموعات ثيمات المنصة، مرتبة كما تظهر في إعدادات المنصة. */
export const PLATFORM_THEME_GROUPS: PlatformThemeGroup[] = [
  {
    id: 'day',
    label: 'ثيمات نهارية',
    hint: 'أسطح واضحة وحدود دقيقة وشرائط هادئة',
    icon: 'sun',
    items: [
      {
        id: 'platform-day-executive',
        name: 'تنفيذي',
        hint: 'عاجي وكحلي وذهبي — حضور مؤسسي',
        primary: '#1e3a8a',
        accent: '#b45309',
        radius: 'sm',
        mode: 'light',
        fontFamily: FONT_SYSTEM,
        sidebarTone: 'primary',
        surfaceStyle: 'outlined',
        bgStyle: 'plain',
        backgroundLight: '#f7f6f2',
        surfaceLight: '#fffefa',
        textLight: '#172033',
      },
      {
        id: 'platform-day-skyline',
        name: 'أفق',
        hint: 'أزرق كهربي وسماوي على أبيض بارد',
        primary: '#2563eb',
        accent: '#0891b2',
        radius: 'md',
        mode: 'light',
        fontFamily: FONT_CAIRO,
        sidebarTone: 'tinted',
        surfaceStyle: 'soft',
        bgStyle: 'gradient',
        backgroundLight: '#eef4ff',
        surfaceLight: '#ffffff',
        textLight: '#0b1e3a',
      },
      {
        id: 'platform-day-jade',
        name: 'يشب',
        hint: 'أخضر مؤسسي ونحاسي دافئ',
        primary: '#0f766e',
        accent: '#c2410c',
        radius: 'lg',
        mode: 'light',
        fontFamily: FONT_ALMARAI,
        sidebarTone: 'tinted',
        surfaceStyle: 'soft',
        bgStyle: 'mesh',
        backgroundLight: '#effaf7',
        surfaceLight: '#fbfffd',
        textLight: '#062a26',
      },
      {
        id: 'platform-day-porcelain',
        name: 'بورسلين',
        hint: 'حبر ليلي وفيروزي على رمادي بارد',
        primary: '#0f172a',
        accent: '#0d9488',
        radius: 'sm',
        mode: 'light',
        fontFamily: FONT_IBM,
        sidebarTone: 'primary',
        surfaceStyle: 'outlined',
        bgStyle: 'plain',
        backgroundLight: '#f4f6fb',
        surfaceLight: '#ffffff',
        textLight: '#0a1120',
      },
      {
        id: 'platform-day-clay',
        name: 'طين',
        hint: 'طيني محروق وأخضر داكن — دافئ ومنظّم',
        primary: '#9a3412',
        accent: '#0f766e',
        radius: 'lg',
        mode: 'light',
        fontFamily: FONT_TAJAWAL,
        sidebarTone: 'tinted',
        surfaceStyle: 'soft',
        bgStyle: 'gradient',
        backgroundLight: '#fbf5ee',
        surfaceLight: '#fffdf9',
        textLight: '#2b1a10',
      },
    ],
  },
  {
    id: 'night',
    label: 'ثيمات ليلية',
    hint: 'خلفيات عميقة وألوان تشغيل عالية الوضوح',
    icon: 'moon',
    items: [
      {
        id: 'platform-night-command',
        name: 'غرفة القيادة',
        hint: 'كحلي داكن وسماوي نيون ولمسة ليمونية',
        primary: '#38bdf8',
        accent: '#a3e635',
        radius: 'md',
        mode: 'dark',
        fontFamily: FONT_IBM,
        sidebarTone: 'tinted',
        surfaceStyle: 'glass',
        bgStyle: 'gradient',
        backgroundDark: '#060b13',
        surfaceDark: '#0d1826',
        textDark: '#e9f5ff',
      },
      {
        id: 'platform-night-onyx',
        name: 'أونيكس',
        hint: 'أسود معدني وعنبري وسماوي',
        primary: '#f59e0b',
        accent: '#38bdf8',
        radius: 'sm',
        mode: 'dark',
        fontFamily: FONT_SYSTEM,
        sidebarTone: 'surface',
        surfaceStyle: 'outlined',
        bgStyle: 'plain',
        backgroundDark: '#08090c',
        surfaceDark: '#12161d',
        textDark: '#f4f7fb',
      },
      {
        id: 'platform-night-abyss',
        name: 'هاوية',
        hint: 'أسود مخضر وزمردي وبنفسجي',
        primary: '#14b8a6',
        accent: '#6366f1',
        radius: 'lg',
        mode: 'dark',
        fontFamily: FONT_ALMARAI,
        sidebarTone: 'tinted',
        surfaceStyle: 'soft',
        bgStyle: 'mesh',
        backgroundDark: '#040d12',
        surfaceDark: '#0a1b20',
        textDark: '#e6fbff',
      },
      {
        id: 'platform-night-royal',
        name: 'ملكي',
        hint: 'بنفسجي ملكي وذهبي على ليل عميق',
        primary: '#a855f7',
        accent: '#f59e0b',
        radius: 'md',
        mode: 'dark',
        fontFamily: FONT_CAIRO,
        sidebarTone: 'tinted',
        surfaceStyle: 'soft',
        bgStyle: 'gradient',
        backgroundDark: '#0a0713',
        surfaceDark: '#151022',
        textDark: '#f5ecff',
      },
      {
        id: 'platform-night-slate',
        name: 'حجر',
        hint: 'رمادي معدني وسماوي فولاذي',
        primary: '#94a3b8',
        accent: '#22d3ee',
        radius: 'sm',
        mode: 'dark',
        fontFamily: FONT_IBM,
        sidebarTone: 'primary',
        surfaceStyle: 'outlined',
        bgStyle: 'plain',
        backgroundDark: '#0a0e13',
        surfaceDark: '#141a21',
        textDark: '#eef3f8',
      },
    ],
  },
  {
    id: 'day-gradient',
    label: 'تدريجات نهارية',
    hint: 'خلفيات متعددة الألوان بشفافية مضبوطة',
    icon: 'sun',
    items: [
      {
        id: 'platform-day-gradient-sky',
        name: 'سماء مفتوحة',
        hint: 'أزرق وبنفسجي ونعناعي فاتح',
        primary: '#1d4ed8',
        accent: '#0891b2',
        radius: 'lg',
        mode: 'light',
        family: 'gradient',
        fontFamily: FONT_IBM,
        sidebarTone: 'tinted',
        surfaceStyle: 'soft',
        bgStyle: 'spectrum',
        gradientLight: ['#e6f2ff', '#edf0ff', '#e6fbf8'],
        backgroundLight: '#eef7ff',
        surfaceLight: '#ffffff',
        textLight: '#082032',
      },
      {
        id: 'platform-day-gradient-dawn',
        name: 'فجر',
        hint: 'خوخي ووردي وسماوي في تدرّج واحد',
        primary: '#7c2d12',
        accent: '#4f46e5',
        radius: 'lg',
        mode: 'light',
        family: 'gradient',
        fontFamily: FONT_TAJAWAL,
        sidebarTone: 'tinted',
        surfaceStyle: 'soft',
        bgStyle: 'spectrum',
        gradientLight: ['#fff2dd', '#f6e8ff', '#e2f7ff'],
        backgroundLight: '#fff3ee',
        surfaceLight: '#fffdfb',
        textLight: '#2a1410',
      },
      {
        id: 'platform-day-gradient-glass',
        name: 'زجاج',
        hint: 'سماوي وفضي وبنفسجي بلمسة زجاجية',
        primary: '#0e7490',
        accent: '#6366f1',
        radius: 'lg',
        mode: 'light',
        family: 'gradient',
        fontFamily: FONT_CAIRO,
        sidebarTone: 'tinted',
        surfaceStyle: 'glass',
        bgStyle: 'spectrum',
        gradientLight: ['#e5fbff', '#eef3ff', '#f3efff'],
        backgroundLight: '#eef9ff',
        surfaceLight: '#fcffff',
        textLight: '#08202b',
      },
      {
        id: 'platform-day-gradient-garden',
        name: 'حديقة',
        hint: 'نعناعي وليموني وفيروزي',
        primary: '#15803d',
        accent: '#ca8a04',
        radius: 'md',
        mode: 'light',
        family: 'gradient',
        fontFamily: FONT_ALMARAI,
        sidebarTone: 'tinted',
        surfaceStyle: 'soft',
        bgStyle: 'spectrum',
        gradientLight: ['#e9fdf2', '#f1fce9', '#e2fbf6'],
        backgroundLight: '#effcf5',
        surfaceLight: '#fbfffd',
        textLight: '#06291d',
      },
      {
        id: 'platform-day-gradient-lilac',
        name: 'ليلك',
        hint: 'أرجواني ووردي وأزرق بارد',
        primary: '#6d28d9',
        accent: '#0d9488',
        radius: 'lg',
        mode: 'light',
        family: 'gradient',
        fontFamily: FONT_CAIRO,
        sidebarTone: 'tinted',
        surfaceStyle: 'soft',
        bgStyle: 'spectrum',
        gradientLight: ['#f4e9ff', '#ffeaf5', '#e7f1ff'],
        backgroundLight: '#f8efff',
        surfaceLight: '#ffffff',
        textLight: '#211034',
      },
    ],
  },
  {
    id: 'night-gradient',
    label: 'تدريجات ليلية',
    hint: 'تدرجات عميقة تناسب لوحات التحكم ليلًا',
    icon: 'moon',
    items: [
      {
        id: 'platform-night-gradient-orbit',
        name: 'مدار',
        hint: 'كحلي ونيلي وفيروزي عميق',
        primary: '#22d3ee',
        accent: '#818cf8',
        radius: 'lg',
        mode: 'dark',
        family: 'gradient',
        fontFamily: FONT_IBM,
        sidebarTone: 'tinted',
        surfaceStyle: 'soft',
        bgStyle: 'spectrum',
        gradientDark: ['#07101f', '#0a1830', '#06202a'],
        backgroundDark: '#081322',
        surfaceDark: '#0d1a2c',
        textDark: '#eaf4ff',
      },
      {
        id: 'platform-night-gradient-neon',
        name: 'TaskFlow',
        hint: 'كحلي عميق وأزرق سماوي وأخضر ليموني — هوية الأيقونة',
        primary: '#45c6ff',
        accent: '#a3e635',
        radius: 'lg',
        mode: 'dark',
        family: 'gradient',
        fontFamily: FONT_CAIRO,
        sidebarTone: 'tinted',
        surfaceStyle: 'glass',
        bgStyle: 'spectrum',
        gradientDark: ['#06152d', '#0a2344', '#0a2e3f'],
        backgroundDark: '#061226',
        surfaceDark: '#0c1d36',
        textDark: '#edf6ff',
      },
      {
        id: 'platform-night-gradient-carbon',
        name: 'كربون',
        hint: 'فحمي وعنبري وفيروزي هادئ',
        primary: '#f59e0b',
        accent: '#14b8a6',
        radius: 'sm',
        mode: 'dark',
        family: 'gradient',
        fontFamily: FONT_SYSTEM,
        sidebarTone: 'tinted',
        surfaceStyle: 'soft',
        bgStyle: 'spectrum',
        gradientDark: ['#0b0d10', '#11161e', '#0b1516'],
        backgroundDark: '#0c0f13',
        surfaceDark: '#141a21',
        textDark: '#eef4f8',
      },
      {
        id: 'platform-night-gradient-deep',
        name: 'محيط عميق',
        hint: 'فيروزي وأزرق على أزرق أسود',
        primary: '#2dd4bf',
        accent: '#60a5fa',
        radius: 'lg',
        mode: 'dark',
        family: 'gradient',
        fontFamily: FONT_ALMARAI,
        sidebarTone: 'tinted',
        surfaceStyle: 'soft',
        bgStyle: 'spectrum',
        gradientDark: ['#04121a', '#061f2c', '#07162e'],
        backgroundDark: '#05141f',
        surfaceDark: '#0a1e2b',
        textDark: '#e7fbff',
      },
      {
        id: 'platform-night-gradient-ember',
        name: 'جمر',
        hint: 'وردي دافئ وذهبي على ليل نبيذي',
        primary: '#fb7185',
        accent: '#fbbf24',
        radius: 'md',
        mode: 'dark',
        family: 'gradient',
        fontFamily: FONT_TAJAWAL,
        sidebarTone: 'tinted',
        surfaceStyle: 'soft',
        bgStyle: 'spectrum',
        gradientDark: ['#140808', '#1e0a10', '#160a1c'],
        backgroundDark: '#150a0d',
        surfaceDark: '#201016',
        textDark: '#fff0f2',
      },
    ],
  },
];

export const PLATFORM_THEME_PRESETS: ThemePreset[] = PLATFORM_THEME_GROUPS.flatMap(
  (group) => group.items,
);

/** يحوّل ثيم المنصة الجاهز إلى رموز كاملة (بلا خلط ألوان الوضع الآخر). */
export function platformThemeFromPreset(preset: ThemePreset): ThemeTokens {
  const dark = preset.mode === 'dark';
  return {
    ...DEFAULT_THEME,
    primary: preset.primary,
    accent: preset.accent,
    radius: preset.radius,
    mode: preset.mode,
    fontFamily: preset.fontFamily ?? DEFAULT_THEME.fontFamily,
    sidebarTone: preset.sidebarTone,
    surfaceStyle: preset.surfaceStyle,
    bgStyle: preset.bgStyle,
    gradientLight: dark ? undefined : preset.gradientLight,
    gradientDark: dark ? preset.gradientDark : undefined,
    backgroundLight: dark ? undefined : preset.backgroundLight,
    surfaceLight: dark ? undefined : preset.surfaceLight,
    textLight: dark ? undefined : preset.textLight,
    backgroundDark: dark ? preset.backgroundDark : undefined,
    surfaceDark: dark ? preset.surfaceDark : undefined,
    textDark: dark ? preset.textDark : undefined,
  };
}

/** السمة الافتراضية لمساحة المنصة إن لم تُحفظ هوية بعد. */
export const TASKFLOW_PLATFORM_THEME = platformThemeFromPreset(
  PLATFORM_THEME_PRESETS.find((preset) => preset.id === 'platform-night-gradient-neon') ??
    PLATFORM_THEME_PRESETS[0]!,
);

/**
 * مطابقة دقيقة (مع تجاهل الفروق بين null و'' التي تنتج من JSON في قاعدة
 * البيانات) حتى لا يظهر ثيم «نشط» لمجرد تشابه اللون الأساسي.
 */
export function themeMatchesPlatformPreset(
  tokens: ThemeTokens,
  preset: ThemePreset,
): boolean {
  const expected = platformThemeFromPreset(preset);
  const same = (left: string | null | undefined, right: string | null | undefined) =>
    (left ?? '').trim().toLowerCase() === (right ?? '').trim().toLowerCase();
  const sameStops = (left?: string[], right?: string[]) =>
    same((left ?? []).join(','), (right ?? []).join(','));
  return (
    same(tokens.primary, expected.primary) &&
    same(tokens.accent, expected.accent) &&
    tokens.radius === expected.radius &&
    tokens.mode === expected.mode &&
    same(tokens.fontFamily, expected.fontFamily) &&
    same(tokens.backgroundLight, expected.backgroundLight) &&
    same(tokens.backgroundDark, expected.backgroundDark) &&
    same(tokens.surfaceLight, expected.surfaceLight) &&
    same(tokens.surfaceDark, expected.surfaceDark) &&
    same(tokens.textLight, expected.textLight) &&
    same(tokens.textDark, expected.textDark) &&
    same(tokens.sidebarTone, expected.sidebarTone) &&
    same(tokens.surfaceStyle, expected.surfaceStyle) &&
    same(tokens.bgStyle, expected.bgStyle) &&
    sameStops(tokens.gradientLight, expected.gradientLight) &&
    sameStops(tokens.gradientDark, expected.gradientDark)
  );
}
